import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { getRoles } from "store/staff"
import PropTypes from "prop-types"
import { Button, Flex, Input as AntInput, Popover, Switch, Tabs } from "antd"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import styles from "../Calendar.module.css"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { useMediaQuery } from "react-responsive"
import { DEFAULT_RANGE, RANGE_OPTIONS } from "../constants"
import { Text } from "ui/Typography"
import useActiveGuide from "hooks/useActiveClient"
import { CustomSelect } from "ui/Input/Select"
import CustomDrawer from "ui/Drawer"
import EquipmentFilters from "./EquipmentFilters"
import { useSelector } from "react-redux"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import moment from "moment"
import API from "constants/api"

const Header = ({ params, onChangeFilters, handleJournalClick, setIsScheduleTimeOffModalOpen, onFiltersChange }) => {
  const activeBookingType = params.tab || 'all-bookings';

  const { isMobile, isTablet } = useDevice()
  const isWideDesktop = useMediaQuery({ query: "(min-width: 1280px)" })
  const showSearchBar = useMediaQuery({ query: "(min-width: 1440px)" })
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { isActive: active } = useActiveGuide()
  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const date = useMemo(() => (params.date ? moment(params.date) : null), [params.date])
  const experiences = useSelector((state) => state.experiences?.list || [])
  const crewProfiles = useSelector((state) => state.staff?.calendarCrew || [])
  const roles = useSelector((state) => state.staff?.roles?.items || [])
  const dispatch = useDispatch();

  // Fetch roles from Redux when Crew tab is active
  useEffect(() => {
    if (activeBookingType === 'crew') {
      dispatch(getRoles())
    }
  }, [activeBookingType, dispatch])

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [viewPopoverOpen, setViewPopoverOpen] = useState(false)
  const [filters, setFilters] = useState({ experience: [], crew: [], equipment: [] })
  const [crewFilters, setCrewFilters] = useState({ roles: [], crewMembers: [] })
  const [searchQuery, setSearchQuery] = useState("")
  const searchDebounceRef = useRef(null)
  const [equipmentList, setEquipmentList] = useState([])

  useEffect(() => {
    const token = localStorage.getItem("access_token")
    fetch(`${API.BASE_URL}/guide/boats/listing?lang=en-GB`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then((r) => r.json())
      .then((res) => setEquipmentList(res.data || []))
      .catch(() => {})
  }, [])

  // Propagate API-relevant filters to parent whenever they change
  const propagateFilters = useCallback(
    (search) => {
      if (!onFiltersChange) return
      const profile_ids =
        activeBookingType === "crew" ? crewFilters.crewMembers : filters.crew
      const boat_ids =
        activeBookingType === "equipment"
          ? filters.equipmentFilters?.equipment || []
          : filters.equipment || []
      const category_ids =
        activeBookingType === "equipment"
          ? filters.equipmentFilters?.category || []
          : []
      onFiltersChange({ profile_ids, boat_ids, category_ids, experience_ids: filters.experience, search })
    },
    [onFiltersChange, activeBookingType, crewFilters.crewMembers, filters.crew, filters.equipmentFilters, filters.equipment, filters.experience]
  )

  // Non-search filter changes fire immediately
  useEffect(() => {
    propagateFilters(searchQuery)
    // eslint-disable-next-line
  }, [crewFilters.crewMembers, filters.experience, filters.crew, filters.equipment, filters.equipmentFilters, activeBookingType])

  // Search changes are debounced 300ms
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => propagateFilters(searchQuery), 300)
    return () => clearTimeout(searchDebounceRef.current)
    // eslint-disable-next-line
  }, [searchQuery])


  const experienceOptions = useMemo(() => {
    return (experiences || [])
      .map((exp) => ({
        label: exp?.title || exp?.name || exp?.trip_title,
        value: exp?.id
      }))
      .filter((opt) => opt.label && opt.value)
  }, [experiences])

  const crewOptions = useMemo(() => {
    return (crewProfiles || [])
      .filter((p) => p.status?.toLowerCase() === "active")
      .map((p) => ({ label: p.name, value: p.id }))
  }, [crewProfiles])

  const equipmentOptions = useMemo(() => {
    return (equipmentList || [])
      .map((eq) => ({ label: eq.equipment_title, value: eq.id }))
      .filter((opt) => opt.label && opt.value)
  }, [equipmentList])

  const selectedFilters = useMemo(
    () => [
      ...filters.experience.map((value) => {
        const opt = experienceOptions.find((o) => o.value === value)
        return { type: "experience", value, label: opt?.label ?? value }
      }),
      ...filters.crew.map((value) => {
        const opt = crewOptions.find((o) => o.value === value)
        return { type: "crew", value, label: opt?.label ?? value }
      }),
      ...filters.equipment.map((value) => {
        const opt = equipmentOptions.find((o) => o.value === value)
        return { type: "equipment", value, label: opt?.label ?? value }
      })
    ],
    [filters, experienceOptions, crewOptions, equipmentOptions]
  )

  const handleFilterChange = (key) => (values) => {
    setFilters((prev) => ({ ...prev, [key]: values }))
  }

  // Clear experience filter when switching from day to week or month
  useEffect(() => {
    if (params.range !== "date" && filters.experience.length > 0) {
      setFilters((prev) => ({ ...prev, experience: [] }))
    }
  }, [params.range])

  const handleFilterRemove = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: prev[key].filter((item) => item !== value) }))
  }

  const renderFilterTag = (props) => {
    const { label, closable, onClose } = props
    return (
      <span className={styles.filterTag}>
        <span className={styles.filterTagLabel}>{label}</span>
        {closable && (
          <button type="button" className={styles.filterTagRemove} onClick={onClose} aria-label="Remove">
            <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
              <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </span>
    )
  }

  useEffect(() => {
    if (!params.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
  }, [params.range]) //eslint-disable-line

  if (!params.range) return null

  const handleClick = () => {
    navigate(routes.guideBookingNewPath())
  }

  return (
    <Flex vertical className={styles.calendarHeader}>
      {/* 1. Booking type buttons */}
      <Flex gap={10} className={`px-m py-s ${styles.bookingTypeButtons}`}>
        <Button
          size="large"
          type={activeBookingType === 'all-bookings' ? 'primary' : 'default'}
          onClick={() => onChangeFilters({ target: { name: 'tab', value: 'all-bookings' } })}
        >
          All Bookings
        </Button>
        <Button
          size="large"
          type={activeBookingType === 'my-bookings' ? 'primary' : 'default'}
          onClick={() => onChangeFilters({ target: { name: 'tab', value: 'my-bookings' } })}
        >
          My Bookings
        </Button>
        <Button
          size="large"
          type={activeBookingType === 'crew' ? 'primary' : 'default'}
          onClick={() => onChangeFilters({ target: { name: 'tab', value: 'crew' } })}
        >
          Crew
        </Button>
        <Button
          size="large"
          type={activeBookingType === 'equipment' ? 'primary' : 'default'}
          onClick={() => onChangeFilters({ target: { name: 'tab', value: 'equipment' } })}
        >
          Equipment
        </Button>
      </Flex>

      {/* 2. DateSwitcher + Tabs (Day/Week/Month) row — ABOVE filters */}
      <Flex gap={10} align="center" justify="flex-start" className={`px-xl py-m ${styles.calendarActionsWrapper}`} wrap="wrap">
        {/* Desktop: DateSwitcher + Tabs + Legend */}
        {!(isMobile || isTablet) && (
          <Flex align="center" gap={16} style={{ width: '100%' }} wrap="wrap">
            <Flex align="center" gap={16} style={{ flex: 1 }}>
              <DateSwitcher
                className={`${styles.calendarSwitcher}`}
                value={date}
                defaultValue={moment().startOf("day")}
                name="date"
                onChange={onChangeFilters}
                type={params.range}
                isCompleteDateRange={true}
              />
              <div className={styles.tabsContainer} style={{ flexShrink: 0 }}>
                <Tabs
                  activeKey={params.range}
                  onChange={(key) => {
                    onChangeFilters({ target: { name: "range", value: key } })
                  }}
                  className={styles.antDTabs}
                >
                  {Object.entries(RANGE_OPTIONS).map(([range, label]) => (
                    <Tabs.TabPane tab={label} key={range} />
                  ))}
                </Tabs>
              </div>
              {params.range === 'week' && activeBookingType === 'all-bookings' && (
                <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
                  <Switch
                    checked={params.agenda === true || params.agenda === 'true'}
                    onChange={(checked) => onChangeFilters({ target: { name: 'agenda', value: checked } })}
                    size="small"
                  />
                  <Text style={{ whiteSpace: 'nowrap' }}>{t("calendar.agenda_view", "Agenda View")}</Text>
                </Flex>
              )}
            </Flex>
            <Flex align="center" gap={16} className={styles.desktopLegendGroup}>
              <Button
                color="primary"
                type="default"
                variant="outlined"
                className={styles.goToToday}
                onClick={() => {
                  onChangeFilters({ target: { name: 'date', value: moment().format('YYYY-MM-DD') }, extra: { name: 'range', value: 'date' } })
                }}
              >
                {t("calendar.go_to_today", "Go to Today")}
              </Button>
              <Flex gap={8} align="center">
                <Text className={styles.headerLegendLabel}>{t("calendar.legend")}:</Text>
                <Flex gap={16}>
                  <Flex gap={4} className={styles.headerLegendItem}>
                    <span className={styles.headerLegendPaid} />
                    <Text className={styles.headerLegendText}>{t("calendar.legend_paid")}</Text>
                  </Flex>
                  <Flex gap={4} className={styles.headerLegendItem}>
                    <span className={styles.headerLegendOutstanding} />
                    <Text className={styles.headerLegendText}>{t("calendar.legend_outstanding")}</Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        )}

        {/* Tablet (768–1024px): Day/Week/Month tabs + DateSwitcher + icons */}
        {isTablet && (
          <Flex align="center" gap={8} style={{ width: '100%' }}>
            {/* LEFT: Day/Week/Month tab buttons */}
            <Flex align="center" gap={0} style={{ flexShrink: 0 }}>
              {[
                { range: 'date', label: 'Day' },
                { range: 'week', label: 'Week' },
                { range: 'month', label: 'Month' },
              ].map(({ range: r, label }) => (
                <button
                  key={r}
                  type="button"
                  className={`${styles.mobileViewTab} ${params.range === r ? styles.mobileViewTabActive : ''}`}
                  onClick={() => onChangeFilters({ target: { name: 'range', value: r } })}
                >
                  {label}
                </button>
              ))}
            </Flex>
            {/* MIDDLE: DateSwitcher */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <DateSwitcher
                className={`${styles.calendarSwitcher}`}
                value={date}
                defaultValue={moment().startOf("day")}
                name="date"
                onChange={onChangeFilters}
                type={params.range}
                isCompleteDateRange={true}
              />
            </div>
            {/* RIGHT: filter (non-crew) + export icons */}
            <Flex align="center" gap={8}>
              {activeBookingType !== 'crew' && (
                <button
                  type="button"
                  className={styles.mobileHeaderIcon}
                  onClick={() => setMobileFiltersOpen(true)}
                  aria-label="Filters"
                  style={{ position: 'relative', border: 'none', background: 'transparent' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                  {(() => {
                    if (activeBookingType === 'equipment') {
                      const count = (filters.equipmentFilters?.equipment?.length || 0) + (filters.equipmentFilters?.category?.length || 0)
                      return count > 0 && (
                        <span style={{
                          position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16,
                          background: '#1d72f4', color: '#fff', borderRadius: '50%', fontSize: 11,
                          fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 1px 4px 0 rgba(29,114,244,0.18)', zIndex: 2, padding: '0 3px', border: '2px solid #fff',
                        }}>{count}</span>
                      )
                    } else {
                      const count = filters.experience.length + filters.crew.length + filters.equipment.length
                      return count > 0 && (
                        <span style={{
                          position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16,
                          background: '#1d72f4', color: '#fff', borderRadius: '50%', fontSize: 11,
                          fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 1px 4px 0 rgba(29,114,244,0.18)', zIndex: 2, padding: '0 3px', border: '2px solid #fff',
                        }}>{count}</span>
                      )
                    }
                  })()}
                </button>
              )}
              <button
                type="button"
                className={styles.mobileHeaderIcon}
                onClick={() => {}}
                aria-label="Export"
                style={{ border: 'none', background: 'transparent' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              </button>
            </Flex>
          </Flex>
        )}

        {/* Mobile (≤767px): DateSwitcher + view-switcher icon (original design) */}
        {isMobile && (
          <Flex align="center" gap={12} style={{ width: '100%' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <DateSwitcher
                className={`${styles.calendarSwitcher}`}
                value={date}
                defaultValue={moment().startOf("day")}
                name="date"
                onChange={onChangeFilters}
                type={params.range}
                isCompleteDateRange={true}
              />
            </div>
            <Flex align="center" gap={8}>
              {activeBookingType !== 'crew' && (
                <button
                  type="button"
                  className={styles.mobileHeaderIcon}
                  onClick={() => setMobileFiltersOpen(true)}
                  aria-label="Filters"
                  style={{ position: 'relative', border: 'none', background: 'transparent' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                  {(() => {
                    if (activeBookingType === 'equipment') {
                      const count = (filters.equipmentFilters?.equipment?.length || 0) + (filters.equipmentFilters?.category?.length || 0)
                      return count > 0 && (
                        <span style={{
                          position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16,
                          background: '#1d72f4', color: '#fff', borderRadius: '50%', fontSize: 11,
                          fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 1px 4px 0 rgba(29,114,244,0.18)', zIndex: 2, padding: '0 3px', border: '2px solid #fff',
                        }}>{count}</span>
                      )
                    } else {
                      const count = filters.experience.length + filters.crew.length + filters.equipment.length
                      return count > 0 && (
                        <span style={{
                          position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16,
                          background: '#1d72f4', color: '#fff', borderRadius: '50%', fontSize: 11,
                          fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 1px 4px 0 rgba(29,114,244,0.18)', zIndex: 2, padding: '0 3px', border: '2px solid #fff',
                        }}>{count}</span>
                      )
                    }
                  })()}
                </button>
              )}
              <button
                type="button"
                className={styles.mobileHeaderIcon}
                onClick={() => {}}
                aria-label="Export"
                style={{ border: 'none', background: 'transparent' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              </button>
              <Popover
                open={viewPopoverOpen}
                onOpenChange={setViewPopoverOpen}
                trigger="click"
                placement="bottomRight"
                arrow={false}
                overlayInnerStyle={{ padding: 0, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.13)' }}
                content={
                  <div className={styles.viewSwitcherPopup}>
                    {[
                      { label: 'Day View', key: 'day', range: 'date', agenda: false },
                      { label: 'Week View', key: 'week', range: 'week', agenda: false },
                      { label: 'Month View', key: 'month', range: 'month', agenda: false },
                      { label: 'List View', key: 'list', range: 'week', agenda: true },
                    ].map(({ label, key, range, agenda: isAgenda }) => {
                      const isActive = params.range === range && (range !== 'week' || !!(params.agenda === true || params.agenda === 'true') === isAgenda)
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`${styles.viewSwitcherOption} ${isActive ? styles.viewSwitcherOptionActive : ''}`}
                          onClick={() => {
                            onChangeFilters({
                              target: { name: 'range', value: range },
                              extra: { name: 'agenda', value: isAgenda }
                            })
                            setViewPopoverOpen(false)
                          }}
                        >
                          <span className={styles.viewSwitcherOptionLabel}>{label}</span>
                        </button>
                      )
                    })}
                  </div>
                }
              >
                <button
                  type="button"
                  className={styles.mobileHeaderIcon}
                  aria-label="Switch view"
                  style={{ border: 'none', background: 'transparent', color: '#1d72f4' }}
                >
                  {params.range === 'date' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                    </svg>
                  )}
                  {params.range === 'week' && !(params.agenda === true || params.agenda === 'true') && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="5" height="18" rx="1"/>
                      <rect x="9.5" y="3" width="5" height="18" rx="1"/>
                      <rect x="16" y="3" width="5" height="18" rx="1"/>
                    </svg>
                  )}
                  {params.range === 'month' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/>
                      <rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                  )}
                  {params.range === 'week' && (params.agenda === true || params.agenda === 'true') && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <line x1="3" y1="12" x2="21" y2="12"/>
                      <line x1="3" y1="18" x2="21" y2="18"/>
                    </svg>
                  )}
                </button>
              </Popover>
            </Flex>
          </Flex>
        )}

        {/* Inline crew filters for mobile — Roles + Crew Member shown directly */}
        {(isMobile || isTablet) && activeBookingType === 'crew' && (
          <Flex gap={8} style={{ width: '100%' }} className="px-m py-xs">
            <div style={{ position: 'relative', flex: 1 }}>
              <CustomSelect
                mode="multiple"
                placeholder="Roles"
                value={crewFilters.roles}
                onChange={values => setCrewFilters(prev => ({ ...prev, roles: values }))}
                options={Array.isArray(roles) ? roles.map(role => ({ label: role.name, value: role.id })) : []}
                maxTagCount={0}
                allowClear
                showSearch={false}
                style={{ width: '100%' }}
                tagRender={() => null}
              />
              {crewFilters.roles.length > 0 && (
                <div style={{ position: 'absolute', left: 12, top: 0, height: '100%', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#525455', fontWeight: 400, fontSize: 16 }}>
                  {`Roles (${crewFilters.roles.length})`}
                </div>
              )}
            </div>
            <div style={{ position: 'relative', flex: 1 }}>
              <CustomSelect
                mode="multiple"
                placeholder="Crew Member"
                value={crewFilters.crewMembers}
                onChange={values => setCrewFilters(prev => ({ ...prev, crewMembers: values }))}
                options={(crewProfiles || []).filter(p => p.status?.toLowerCase() === "active").map(profile => ({ label: profile.name, value: profile.id }))}
                maxTagCount={0}
                allowClear
                showSearch={false}
                style={{ width: '100%' }}
                tagRender={() => null}
              />
              {crewFilters.crewMembers.length > 0 && (
                <div style={{ position: 'absolute', left: 12, top: 0, height: '100%', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#525455', fontWeight: 400, fontSize: 16 }}>
                  {`Crew Member (${crewFilters.crewMembers.length})`}
                </div>
              )}
            </div>
          </Flex>
        )}
      </Flex>

      {/* 3. Desktop: inline filters — BELOW the tabs row */}
      {!(isMobile || isTablet) && (
        <Flex justify="space-between" className="px-m py-m" gap={12} wrap="wrap">
          <Flex gap={12} align="center" className={styles.calendarFilters}>
            {activeBookingType === 'crew' ? (
              <>
                {showSearchBar && <AntInput placeholder={t("calendar.search_placeholder")} className={styles.calendarSearch} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear />}
                <div style={{ position: 'relative', width: 180 }}>
                  <CustomSelect
                    mode="multiple"
                    placeholder="Roles"
                    className={styles.calendarFilterSelect}
                    value={crewFilters.roles}
                    onChange={values => setCrewFilters(prev => ({ ...prev, roles: values }))}
                    options={Array.isArray(roles) ? roles.map(role => ({ label: role.name, value: role.id })) : []}
                    maxTagCount={0}
                    allowClear
                    showSearch={false}
                    style={{ width: 180 }}
                    tagRender={() => null}
                  />
                  {crewFilters.roles.length > 0 && (
                    <div style={{ position: 'absolute', left: 12, top: 0, height: '100%', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#525455', fontWeight: 400, fontSize: 16 }}>
                      {`Roles (${crewFilters.roles.length})`}
                    </div>
                  )}
                </div>
                <div style={{ position: 'relative', width: 180 }}>
                  <CustomSelect
                    mode="multiple"
                    placeholder="Crew Member"
                    className={styles.calendarFilterSelect}
                    value={crewFilters.crewMembers}
                    onChange={values => setCrewFilters(prev => ({ ...prev, crewMembers: values }))}
                    options={(crewProfiles || []).filter(p => p.status?.toLowerCase() === "active").map(profile => ({ label: profile.name, value: profile.id }))}
                    maxTagCount={0}
                    allowClear
                    showSearch={false}
                    style={{ width: 180 }}
                    tagRender={() => null}
                  />
                  {crewFilters.crewMembers.length > 0 && (
                    <div style={{ position: 'absolute', left: 12, top: 0, height: '100%', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#525455', fontWeight: 400, fontSize: 16 }}>
                      {`Crew Member (${crewFilters.crewMembers.length})`}
                    </div>
                  )}
                </div>
              </>
            ) : activeBookingType === 'equipment' ? (
              <>
                {showSearchBar && <AntInput placeholder={t("calendar.search_placeholder")} className={styles.calendarSearch} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear />}
                <EquipmentFilters
                  value={filters.equipmentFilters || {}}
                  onChange={(val) => setFilters((prev) => ({ ...prev, equipmentFilters: val }))}
                  onOptionsChange={({ equipmentOptions, categoryOptions }) => {
                    setFilters((prev) => ({ ...prev, equipmentOptions, categoryOptions }));
                  }}
                  hideSearch
                />
              </>
            ) : (
              <>
                {showSearchBar && <AntInput placeholder={t("calendar.search_placeholder")} className={styles.calendarSearch} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear />}
                <div style={{ position: 'relative', width: 180 }}>
                  <CustomSelect
                    mode="multiple"
                    placeholder={t("calendar.filters.experience", "Experience")}
                    className={styles.calendarFilterSelect}
                    value={filters.experience}
                    onChange={handleFilterChange("experience")}
                    options={experienceOptions}
                    maxTagCount={0}
                    allowClear
                    showSearch={false}
                    style={{ width: 180 }}
                    tagRender={() => null}
                  />
                  {filters.experience.length > 0 && (
                    <div style={{ position: 'absolute', left: 12, top: 0, height: '100%', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#525455', fontWeight: 400, fontSize: 16 }}>
                      {`${t("calendar.filters.experience", "Experience")} (${filters.experience.length})`}
                    </div>
                  )}
                </div>
                <div style={{ position: 'relative', width: 180 }}>
                  <CustomSelect
                    mode="multiple"
                    placeholder={t("calendar.filters.crew_member", "Crew Member")}
                    className={styles.calendarFilterSelect}
                    value={filters.crew}
                    onChange={handleFilterChange("crew")}
                    options={crewOptions}
                    maxTagCount={0}
                    allowClear
                    showSearch={false}
                    style={{ width: 180 }}
                    tagRender={() => null}
                  />
                  {filters.crew.length > 0 && (
                    <div style={{ position: 'absolute', left: 12, top: 0, height: '100%', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#525455', fontWeight: 400, fontSize: 16 }}>
                      {`${t("calendar.filters.crew_member", "Crew Member")} (${filters.crew.length})`}
                    </div>
                  )}
                </div>
                <div style={{ position: 'relative', width: 180 }}>
                  <CustomSelect
                    mode="multiple"
                    placeholder={t("calendar.filters.equipment", "Equipment")}
                    className={styles.calendarFilterSelect}
                    value={filters.equipment}
                    onChange={handleFilterChange("equipment")}
                    options={equipmentOptions}
                    maxTagCount={0}
                    allowClear
                    showSearch={false}
                    style={{ width: 180 }}
                    tagRender={() => null}
                  />
                  {filters.equipment.length > 0 && (
                    <div style={{ position: 'absolute', left: 12, top: 0, height: '100%', display: 'flex', alignItems: 'center', pointerEvents: 'none', color: '#525455', fontWeight: 400, fontSize: 16 }}>
                      {`${t("calendar.filters.equipment", "Equipment")} (${filters.equipment.length})`}
                    </div>
                  )}
                </div>
              </>
            )}
          </Flex>
          {isWideDesktop && (
            <Flex gap={10} className={`${styles.calendarActions} ml-auto`}>
              {canCreateJournal && (isMobile || isTablet) && (
                <Button
                  color="primary"
                  variant="outlined"
                  className={`${styles.addJournal} px-m py-s`}
                  size="large"
                  onClick={active ? handleJournalClick : undefined}
                  disabled={!active}
                  aria-disabled={!active}
                >
                  {t("calendar.journal.add_journal")}
                </Button>
              )}
              {(activeBookingType === 'my-bookings' || activeBookingType === 'crew') && (
                <Button
                  color="primary"
                  variant="outlined"
                  className={`${styles.scheduleTimeOff} px-m py-s`}
                  size="large"
                  onClick={active ? () => setIsScheduleTimeOffModalOpen(true) : undefined}
                  disabled={!active}
                  aria-disabled={!active}
                >
                  {t("unavailable_days.schedule_time_off")}
                </Button>
              )}
              {canCreateBooking && (
                <Button
                  type="primary"
                  className={`${styles.newBooking} px-m py-s`}
                  size="large"
                  onClick={active ? handleClick : undefined}
                  disabled={!active}
                  aria-disabled={!active}
                >
                  {t("bookings.add")}
                </Button>
              )}
            </Flex>
          )}
        </Flex>
      )}

      {/* 4. Mobile: filter bottom sheet */}
      {(isMobile || isTablet) && (
        <CustomDrawer open={mobileFiltersOpen} setOpen={setMobileFiltersOpen} title="Filters">
          <Flex vertical gap={16}>
            {activeBookingType === 'crew' ? (
              <>
                <CustomSelect
                  mode="multiple"
                  placeholder={crewFilters.roles.length > 0 ? `Roles (${crewFilters.roles.length})` : "Roles"}
                  className={styles.calendarFilterSelect}
                  value={crewFilters.roles}
                  onChange={values => setCrewFilters(prev => ({ ...prev, roles: values }))}
                  options={Array.isArray(roles) ? roles.map(role => ({ label: role.name, value: role.id })) : []}
                  maxTagCount="responsive"
                  allowClear
                  style={{ width: '100%' }}
                  tagRender={renderFilterTag}
                />
                <CustomSelect
                  mode="multiple"
                  placeholder={crewFilters.crewMembers.length > 0 ? `Crew Member (${crewFilters.crewMembers.length})` : "Crew Member"}
                  className={styles.calendarFilterSelect}
                  value={crewFilters.crewMembers}
                  onChange={values => setCrewFilters(prev => ({ ...prev, crewMembers: values }))}
                  options={(crewProfiles || []).filter(p => p.status?.toLowerCase() === "active").map(profile => ({ label: profile.name, value: profile.id }))}
                  maxTagCount="responsive"
                  allowClear
                  style={{ width: '100%' }}
                  tagRender={renderFilterTag}
                />
              </>
            ) : activeBookingType === 'equipment' ? (
              <EquipmentFilters
                value={filters.equipmentFilters || {}}
                onChange={(val) => setFilters((prev) => ({ ...prev, equipmentFilters: val }))}
                onOptionsChange={({ equipmentOptions, categoryOptions }) => {
                  setFilters((prev) => ({ ...prev, equipmentOptions, categoryOptions }));
                }}
                hideSearch
                fullWidth
              />
            ) : (
              <>
                <CustomSelect
                  mode="multiple"
                  placeholder={t("calendar.filters.crew_member", "Crew Member")}
                  className={styles.calendarFilterSelect}
                  value={filters.crew}
                  onChange={handleFilterChange("crew")}
                  options={crewOptions}
                  maxTagCount="responsive"
                  allowClear
                  style={{ width: '100%' }}
                  tagRender={renderFilterTag}
                />
                <CustomSelect
                  mode="multiple"
                  placeholder={t("calendar.filters.equipment", "Equipment")}
                  className={styles.calendarFilterSelect}
                  value={filters.equipment}
                  onChange={handleFilterChange("equipment")}
                  options={equipmentOptions}
                  maxTagCount="responsive"
                  allowClear
                  style={{ width: '100%' }}
                  tagRender={renderFilterTag}
                />
                {params.range === "date" && (
                  <CustomSelect
                    mode="multiple"
                    placeholder={t("calendar.filters.experience", "Experience")}
                    className={styles.calendarFilterSelect}
                    value={filters.experience}
                    onChange={handleFilterChange("experience")}
                    options={experienceOptions}
                    maxTagCount="responsive"
                    allowClear
                    style={{ width: '100%' }}
                    tagRender={renderFilterTag}
                  />
                )}
              </>
            )}
            <Button type="primary" block style={{ borderRadius: 25, height: 44, marginTop: 8 }} onClick={() => setMobileFiltersOpen(false)}>
              Apply
            </Button>
            <Button
              type="link"
              block
              onClick={() => {
                if (activeBookingType === 'crew') {
                  setCrewFilters({ roles: [], crewMembers: [] })
                } else if (activeBookingType === 'equipment') {
                  setFilters(prev => ({ ...prev, equipmentFilters: { equipment: [], category: [] } }))
                } else {
                  setFilters({ experience: [], crew: [], equipment: [] })
                }
                setMobileFiltersOpen(false)
              }}
              style={{ color: '#1d72f4' }}
            >
              Clear Filters
            </Button>
          </Flex>
        </CustomDrawer>
      )}

      {/* 5. Selected filter chips */}
      {activeBookingType === 'crew' && (crewFilters.roles.length > 0 || crewFilters.crewMembers.length > 0) && (
        <Flex gap={8} className={styles.selectedFilters} wrap="wrap">
          {crewFilters.roles.map(roleId => {
            const roleObj = (Array.isArray(roles) ? roles : []).find(r => r.id === roleId)
            return (
              <span key={`role-${roleId}`} className={styles.filterChip}>
                <span className={styles.filterChipLabel}>{roleObj ? roleObj.name : roleId}</span>
                <button
                  type="button"
                  className={styles.filterChipRemove}
                  onClick={() => setCrewFilters(prev => ({ ...prev, roles: prev.roles.filter(id => id !== roleId) }))}
                  aria-label="Remove"
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            )
          })}
          {crewFilters.crewMembers.map(memberId => {
            const memberObj = (Array.isArray(crewProfiles) ? crewProfiles : []).find(m => m.id === memberId)
            return (
              <span key={`crew-${memberId}`} className={styles.filterChip}>
                <span className={styles.filterChipLabel}>{memberObj ? memberObj.name : memberId}</span>
                <button
                  type="button"
                  className={styles.filterChipRemove}
                  onClick={() => setCrewFilters(prev => ({ ...prev, crewMembers: prev.crewMembers.filter(id => id !== memberId) }))}
                  aria-label="Remove"
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            )
          })}
        </Flex>
      )}
      {activeBookingType === 'equipment' && filters.equipmentFilters && (filters.equipmentFilters.equipment || filters.equipmentFilters.category) && (
        <Flex gap={8} className={styles.selectedFilters} wrap="wrap">
          {Array.isArray(filters.equipmentFilters.equipment) && filters.equipmentFilters.equipment.map(eqId => {
            const eqObj = (filters.equipmentOptions || []).find(eq => eq.value === eqId);
            return (
              <span key={`equipment-${eqId}`} className={styles.filterChip}>
                <span className={styles.filterChipLabel}>{eqObj ? eqObj.label : eqId}</span>
                <button
                  type="button"
                  className={styles.filterChipRemove}
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    equipmentFilters: {
                      ...prev.equipmentFilters,
                      equipment: prev.equipmentFilters.equipment.filter(id => id !== eqId)
                    }
                  }))}
                  aria-label="Remove"
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            );
          })}
          {Array.isArray(filters.equipmentFilters.category) && filters.equipmentFilters.category.map(catId => {
            const catObj = (filters.categoryOptions || []).find(cat => cat.value === catId);
            return (
              <span key={`category-${catId}`} className={styles.filterChip}>
                <span className={styles.filterChipLabel}>{catObj ? catObj.label : catId}</span>
                <button
                  type="button"
                  className={styles.filterChipRemove}
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    equipmentFilters: {
                      ...prev.equipmentFilters,
                      category: prev.equipmentFilters.category.filter(id => id !== catId)
                    }
                  }))}
                  aria-label="Remove"
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            );
          })}
        </Flex>
      )}
      {activeBookingType !== 'crew' && activeBookingType !== 'equipment' && selectedFilters.length > 0 && (
        <Flex gap={8} className={styles.selectedFilters} wrap="wrap">
          {selectedFilters.map((filter) => (
            <span key={`${filter.type}-${filter.value}`} className={styles.filterChip}>
              <span className={styles.filterChipLabel}>{filter.label}</span>
              <button
                type="button"
                className={styles.filterChipRemove}
                onClick={() => handleFilterRemove(filter.type, filter.value)}
                aria-label="Remove"
              >
                <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                  <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
        </Flex>
      )}

      {/* 6. Mobile legend row */}
      {(isMobile || isTablet) && (
        <Flex gap={12} align="center" justify="space-between" className={`px-s py-xs ${styles.mobileLegendRow}`}>
          <Flex gap={12} align="center">
            <Text className={styles.headerLegendLabel}>{t("calendar.legend")}:</Text>
            <Flex gap={16}>
              <Flex gap={4} className={styles.headerLegendItem}>
                <span className={styles.headerLegendPaid} />
                <Text className={styles.headerLegendText}>{t("calendar.legend_paid")}</Text>
              </Flex>
              <Flex gap={4} className={styles.headerLegendItem}>
                <span className={styles.headerLegendOutstanding} />
                <Text className={styles.headerLegendText}>{t("calendar.legend_outstanding")}</Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

Header.propTypes = {
  params: PropTypes.shape({
    date: PropTypes.string,
    range: PropTypes.string,
    weather: PropTypes.bool
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  handleJournalClick: PropTypes.func.isRequired,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func
}

export default Header
